import type { FaqRichTextFormat } from './types';

export const EMPTY_TIPTAP_DOC = JSON.stringify({
  type: 'doc',
  content: [{ type: 'paragraph' }],
});

type TiptapNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
};

function parseTiptapDoc(value?: string | null): TiptapNode | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as TiptapNode;
    return parsed?.type === 'doc' ? parsed : null;
  } catch {
    return null;
  }
}

export function isTiptapDoc(value?: string | null) {
  return parseTiptapDoc(value) !== null;
}

export function textToTiptapDoc(text?: string | null) {
  const lines = (text ?? '').replace(/\r\n/g, '\n').split('\n');
  const content = lines.length > 0
    ? lines.map((line) => line.trim()
      ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
      : { type: 'paragraph' })
    : [{ type: 'paragraph' }];

  return JSON.stringify({ type: 'doc', content });
}

export function ensureTiptapContent(content?: string | null, format?: FaqRichTextFormat | string | null) {
  if (format === 'tiptap' && isTiptapDoc(content)) {
    return content || EMPTY_TIPTAP_DOC;
  }
  if (isTiptapDoc(content)) {
    return content || EMPTY_TIPTAP_DOC;
  }
  return textToTiptapDoc(content);
}

function collectText(node: TiptapNode, parts: string[]) {
  if (node.type === 'text') {
    parts.push(node.text ?? '');
    return;
  }

  if (node.type === 'hardBreak') {
    parts.push('\n');
    return;
  }

  if (node.type === 'image') {
    const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
    parts.push(alt ? `[图片: ${alt}]` : '[图片]');
    return;
  }

  for (const child of node.content ?? []) {
    collectText(child, parts);
  }

  if (['paragraph', 'heading', 'blockquote', 'listItem'].includes(node.type ?? '')) {
    parts.push('\n');
  }
}

function hasImageNode(node: TiptapNode): boolean {
  if (node.type === 'image') return true;
  return (node.content ?? []).some(hasImageNode);
}

export function tiptapToPlainText(value?: string | null) {
  const parsed = parseTiptapDoc(value);
  if (!parsed) return (value ?? '').trim();

  const parts: string[] = [];
  collectText(parsed, parts);
  return parts
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function htmlToPlainText(value?: string | null) {
  const html = value ?? '';
  if (!html.trim()) return '';

  if (typeof document !== 'undefined') {
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('script, style, noscript').forEach((el) => el.remove());
    return (template.content.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<noscript[\s\S]*?<\/noscript>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function faqAnswerToPlainText(value?: string | null, format?: string | null) {
  return format === 'html' ? htmlToPlainText(value) : tiptapToPlainText(value);
}

export function richTextHasContent(value?: string | null) {
  const parsed = parseTiptapDoc(value);
  return tiptapToPlainText(value).trim().length > 0 || (parsed ? hasImageNode(parsed) : false);
}
